import { useEffect, useState, useMemo } from "react";
import { api } from "@/api/client";
import { PageHeader, EmptyState } from "@/components/common";
import { Plus, Edit, Trash2, Loader2, Search, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/confirm";

export default function Roles() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [perms, setPerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", permissions: [] });

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([api.get("/roles"), api.get("/permissions")]);
      setRows(r.data?.data || []);
      setPerms(p.data?.data?.permissions || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/roles/${editing.id}`, form);
      else await api.post("/roles", form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.message || "Failed"); }
  };

  const remove = async (r) => {
    if (!(await confirmDialog({ title: "Delete role", message: `Delete role "${r.name}"?`, confirmText: "Delete", danger: true }))) return;
    try { await api.delete(`/roles/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
  };

  const toggle = (p) => {
    setForm(f => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter(x => x !== p) : [...f.permissions, p] }));
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };
  const SortTh = ({ label, k }) => (
    <th onClick={() => toggleSort(k)} className="cursor-pointer select-none group">
      <div className="flex items-center gap-1.5">{label}
        {sortKey === k ? (sortDir === "asc" ? <ChevronUp size={11} className="text-[#334E7B]"/> : <ChevronDown size={11} className="text-[#334E7B]"/>)
          : <ChevronsUpDown size={11} className="opacity-0 group-hover:opacity-50 transition-opacity"/>}
      </div>
    </th>
  );

  const displayed = useMemo(() => {
    let data = rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(r => Object.values(r).some(v => v && String(v).toLowerCase().includes(q)));
    }
    if (sortKey) {
      data = [...data].sort((a, b) => {
        const va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
        const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return data;
  }, [rows, search, sortKey, sortDir]);

  return (
    <div data-testid="roles-page">
      <PageHeader title="Roles & Permissions" breadcrumbs={["Masters", "Roles"]}
        actions={<button onClick={() => { setEditing(null); setForm({ code: "", name: "", permissions: [] }); setOpen(true); }} className="erp-btn-primary inline-flex items-center gap-2" data-testid="new-role-btn"><Plus size={14}/> New Role</button>}
      />

      <div className="erp-card p-3 mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search roles..." className="erp-input pl-8 text-sm" />
          {search && <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={12}/></button>}
        </div>
        <span className="text-xs text-slate-500">{displayed.length} results</span>
      </div>

      <div className="erp-card overflow-x-auto">
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div> :
         rows.length === 0 ? <EmptyState /> : (
          <table className="erp-table">
            <thead><tr><SortTh label="Code" k="code"/><SortTh label="Name" k="name"/><th>Permissions</th><th>System</th><th></th></tr></thead>
            <tbody>
              {displayed.map(r => (
                <tr key={r.id}>
                  <td className="font-mono text-xs">{r.code}</td>
                  <td className="font-medium">{r.name}</td>
                  <td className="text-xs text-slate-600">{(r.permissions || []).slice(0, 4).join(", ")}{(r.permissions || []).length > 4 ? `… +${r.permissions.length - 4}` : ""}</td>
                  <td>{r.is_system ? <span className="badge-base bg-slate-50 text-slate-600 border-slate-200">System</span> : null}</td>
                  <td>
                    <div className="flex justify-end gap-1">
                      <button onClick={() => { setEditing(r); setForm({ code: r.code, name: r.name, permissions: r.permissions || [] }); setOpen(true); }} className="p-1.5 hover:bg-slate-100 rounded-sm"><Edit size={14}/></button>
                      {!r.is_system && <button onClick={() => remove(r)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm"><Trash2 size={14}/></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Role" : "New Role"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Code *</label>
                  <input value={form.code} onChange={e => setForm({...form, code: e.target.value})} className="erp-input mt-1" required disabled={!!editing} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Name *</label>
                  <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="erp-input mt-1" required />
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Permissions</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto p-3 border border-slate-200 rounded-sm bg-slate-50">
                  {perms.map(p => (
                    <label key={p} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white p-1 rounded-sm">
                      <input type="checkbox" checked={form.permissions.includes(p)} onChange={() => toggle(p)} />
                      <span className="font-mono">{p}</span>
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <button type="button" onClick={() => setOpen(false)} className="erp-btn-secondary">Cancel</button>
                <button type="submit" className="erp-btn-primary">{editing ? "Update" : "Create"}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
